/**
 * 🎓 EVENT EMITTER - SYNCHRONISATION PÉDAGOGIQUE
 * ================================================
 * 
 * Système d'événements pour synchroniser les composants
 * après une évaluation Owen.
 * 
 * Pattern: EventEmitter global pour découpler les composants.
 * Les pages de formation émettent, les composants d'affichage écoutent.
 */

class PedagogicalEventEmitter {
  constructor() {
    this.listeners = new Map();
  }

  /**
   * S'abonner à un événement pédagogique
   * @param {string} event - Nom de l'événement
   * @param {Function} callback - Fonction à appeler
   * @returns {Function} Fonction de désabonnement
   */
  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Retourner la fonction de désabonnement
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  /**
   * Émettre un événement pédagogique
   * @param {string} event - Nom de l'événement
   * @param {Object} data - Données de l'événement
   */
  emit(event, data = {}) {
    console.log(`[PedagogicalEvents] 📡 Émission: ${event}`, data);
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[PedagogicalEvents] ❌ Erreur callback ${event}:`, error);
        }
      });
    }
  }

  /**
   * S'abonner une seule fois à un événement
   */
  once(event, callback) {
    const unsubscribe = this.subscribe(event, (data) => {
      unsubscribe();
      callback(data);
    });
    return unsubscribe;
  }
}

// Instance singleton
const pedagogicalEvents = new PedagogicalEventEmitter();

// Événements disponibles
export const PEDAGOGICAL_EVENTS = {
  // Déclenché après une évaluation Owen réussie
  EVALUATION_COMPLETE: 'evaluation_complete',
  // Déclenché quand les objectifs doivent être rechargés
  OBJECTIVES_REFRESH_NEEDED: 'objectives_refresh_needed',
  // Déclenché quand un module est validé
  MODULE_VALIDATED: 'module_validated',
  // Déclenché quand une session se termine
  SESSION_ENDED: 'session_ended'
};

/**
 * Hook pour utiliser les événements pédagogiques dans un composant
 * @example
 * // Dans un composant React:
 * useEffect(() => {
 *   const unsubscribe = subscribeToEvaluation((data) => {
 *     refreshObjectives();
 *   });
 *   return unsubscribe;
 * }, []);
 */
export const subscribeToEvaluation = (callback) => {
  return pedagogicalEvents.subscribe(PEDAGOGICAL_EVENTS.EVALUATION_COMPLETE, callback);
};

export const subscribeToRefresh = (callback) => {
  return pedagogicalEvents.subscribe(PEDAGOGICAL_EVENTS.OBJECTIVES_REFRESH_NEEDED, callback);
};

/**
 * Notifier qu'une évaluation Owen est terminée
 * @param {Object} result - Résultat de l'évaluation
 */
export const notifyEvaluationComplete = (result) => {
  // 🔒 P1.5-A MOA (Jan 2026): Émission UNIQUE d'événement
  // Suppression de l'émission double qui causait le clignotement
  // SEUL l'événement EVALUATION_COMPLETE est émis
  // Les composants décident individuellement s'ils doivent rafraîchir
  console.log('[PedagogicalEvents] 🎓 P1.5-A: Notification évaluation (événement unique)', {
    module_id: result?.module_id,
    new_evidences: result?.new_evidences_count,
    timestamp: new Date().toISOString()
  });
  
  pedagogicalEvents.emit(PEDAGOGICAL_EVENTS.EVALUATION_COMPLETE, result);
  // 🔒 P1.5-A: SUPPRESSION de l'émission double OBJECTIVES_REFRESH_NEEDED
  // Cette double émission causait 2 re-fetch et le clignotement visuel
};

/**
 * Notifier qu'un refresh des objectifs est nécessaire
 */
export const notifyRefreshNeeded = (moduleId) => {
  pedagogicalEvents.emit(PEDAGOGICAL_EVENTS.OBJECTIVES_REFRESH_NEEDED, {
    moduleId,
    reason: 'manual'
  });
};

/**
 * Notifier qu'un module a été validé
 */
export const notifyModuleValidated = (moduleId) => {
  pedagogicalEvents.emit(PEDAGOGICAL_EVENTS.MODULE_VALIDATED, { moduleId });
};

export default pedagogicalEvents;
